import {
  ExternalServiceName,
  IntegrationDataErrorType,
} from 'lib/types/partnership';
import _ from 'lodash';

/**
 * Returns the appropriate help article for a specified external service.
 */
export const getHelpArticleUrl = (serviceName: ExternalServiceName): string => {
  switch (serviceName) {
    case ExternalServiceName.BAMBOOHR:
      return 'https://help.goethena.com/hc/en-us/articles/4413431832973-BambooHR-FAQ';
    case ExternalServiceName.GUSTO:
      return 'https://help.goethena.com/hc/en-us/articles/4403023205517-Gusto-Integration-FAQ';
    case ExternalServiceName.RIPPLING:
      return 'https://help.goethena.com/hc/en-us/articles/4403097431693-Rippling-Integration-FAQ';
    default:
      return 'https://help.goethena.com/hc/en-us/categories/360006343571-Integrations';
  }
};

/**
 * Returns a nicely formatted string summary of an integration data error.
 */
export const getIntegrationDataErrorMessage = (dataError: {
  errorType: IntegrationDataErrorType;
  errorDetails: Record<string, any>;
}): string => {
  const { errorType, errorDetails } = dataError;

  const getAccountAdminDisabledMessage = () => {
    const { ethenaUserEmail } = errorDetails;
    return `Account admin ${ethenaUserEmail} must be disabled manually`;
  };

  const getDuplicateEthenaUserMatchMessage = () => {
    const { duplicates } = errorDetails;
    return `${duplicates.length} external users matched`;
  };

  const getDuplicatePartnershipEmailMessage = () => {
    const { duplicates } = errorDetails;
    return `${duplicates.length} external users with this email`;
  };

  const getInvalidDataMessage = () => {
    const { invalidData } = errorDetails;
    const invalidDataStr = _.map(invalidData, (i) => i.fieldName).join(', ');
    return `Invalid value for field: ${invalidDataStr}`;
  };

  const getLeaveAlreadyStartedMessage = () => {
    return 'Cannot make changes when leave has started';
  };

  const getManagerNotFoundMessage = () => {
    const { affectedReports } = errorDetails;
    return `Manager Not Found, Affected reports ${affectedReports.length}`;
  };

  const getMissingRequiredInformationMessage = () => {
    const { missingFields } = errorDetails;
    return `Missing ${missingFields.join(', ')}`;
  };

  switch (errorType) {
    case IntegrationDataErrorType.ACCOUNT_ADMIN_DISABLED:
      return getAccountAdminDisabledMessage();
    case IntegrationDataErrorType.DUPLICATE_ETHENA_USER_MATCH:
      return getDuplicateEthenaUserMatchMessage();
    case IntegrationDataErrorType.DUPLICATE_PARTNERSHIP_EMAIL:
      return getDuplicatePartnershipEmailMessage();
    case IntegrationDataErrorType.INVALID_DATA:
      return getInvalidDataMessage();
    case IntegrationDataErrorType.LEAVE_ALREADY_STARTED:
      return getLeaveAlreadyStartedMessage();
    case IntegrationDataErrorType.MANAGER_NOT_FOUND:
      return getManagerNotFoundMessage();
    case IntegrationDataErrorType.MISSING_REQUIRED_INFORMATION:
      return getMissingRequiredInformationMessage();
    default:
      return 'An unknown error occurred';
  }
};

/**
 * Returns `true` if the specified HRIS service name is a Workday integration.
 */
export const getIsWorkdayServiceName = (
  serviceName: ExternalServiceName
): boolean => _.includes(serviceName, 'WORKDAY');
